import React, { useState, useEffect } from 'react';
import {
  Modal,
  ButtonField,
  Icon,
  SrOnly,
  Fullscreen,
} from '@wk/components-v3-react16';
import { Logs } from './LogType';
import LogTable from './LogTable/LogTable';
import styles from './ModalTable.module.scss';

interface ModalTableProperties {
  fileName: string;
  isDownloading: boolean;
  isOpen: boolean;
  logs: Logs;
  onClose: () => void;
  onDownload: () => void;
  originalStartTime: string;
}

const ModalTable = ({
  isOpen,
  logs,
  fileName,
  onClose,
  onDownload,
  isDownloading,
  originalStartTime,
}: ModalTableProperties) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Reset fullscreen state when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsFullscreen(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    onClose();
    // Reset fullscreen after modal close animation completes
    setTimeout(() => {
      setIsFullscreen(false);
    }, 400);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <Modal
      isOpen={isOpen}
      srLabelledBy="log-modal-header"
      onUserRequest={handleClose}
    >
      <Fullscreen isFullscreen={isFullscreen}>
        <div className={styles.modalContent}>
          <div className={styles.header}>
            <h1 id="log-modal-header">{fileName}</h1>
            <div className={styles.headerButtons}>
              <ButtonField type="icon" size="large">
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  flow-id="fullscreen-button"
                >
                  <Icon
                    name={
                      isFullscreen
                        ? 'wk-icon-contract-angle'
                        : 'wk-icon-expand-angle'
                    }
                  />
                  <SrOnly>
                    {isFullscreen ? 'Close fullscreen' : 'Open fullscreen'}
                  </SrOnly>
                </button>
              </ButtonField>
              <ButtonField type="icon" size="large">
                <button
                  type="button"
                  onClick={handleClose}
                  flow-id="close-button"
                >
                  <Icon name="wk-icon-close" />
                  <SrOnly>Close modal</SrOnly>
                </button>
              </ButtonField>
            </div>
          </div>
          <div className={styles.content}>
            <LogTable logs={logs} originalStartTime={originalStartTime} />
          </div>
          <div className={styles.footer}>
            <ButtonField type="text">
              <button type="button" onClick={handleClose}>
                Close
              </button>
            </ButtonField>
            <ButtonField type="secondary">
              <button
                type="button"
                onClick={onDownload}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <>
                    <Icon name="wk-icon-refresh" isSpin={true} />
                    <span>Downloading...</span>
                  </>
                ) : (
                  <>
                    <Icon name="wk-icon-download-line" />
                    <span>Download Log</span>
                  </>
                )}
              </button>
            </ButtonField>
          </div>
        </div>
      </Fullscreen>
    </Modal>
  );
};

export default ModalTable;
